/**
 * Store of the gobal leave page confirm message
 *  - Used by component /src/App.vue to display a confirm message about leaving the current page
 *  - Used by the beforeEach router (src/plugins/router.ts) to show the leave page confirm message if it is activated
 * Use method activate(true/false) in components in order to activate/deactivate the leave page confirm message
 */
import { defineStore } from "pinia";
import type { RouteLocationNormalized } from "vue-router";
import { router } from "~/plugins/router";

const DEFAULT_CONFIRM_MESSAGE =
  "message.confirm.leave.page.message.unsavedData";
const DEFAULT_TITLE = "message.confirm.leave.page.title";

export const useConfirmLeavePageStore = defineStore(
  "useConfirmLeavePageStore",
  () => {
    const state = reactive<{
      count: number;
      shown: boolean;
      toFullPath?: string | null;
      messageConfirmUnsavedData: string;
      title: string;
      excludedRoutes: string[];
    }>({
      count: 0,
      shown: false,
      toFullPath: undefined,
      messageConfirmUnsavedData: DEFAULT_CONFIRM_MESSAGE,
      title: DEFAULT_TITLE,
      excludedRoutes: [],
    });

    /**
     * Check if the leave page confirm message is activated and must be displayed
     * @returns true if leave page confirm is activated, false otherwise
     */
    function isActivated(to?: RouteLocationNormalized) {
      if (to && state.excludedRoutes.includes(to.path as string)) {
        return false;
      }
      return state.count > 0;
    }

    /**
     * Activate/deactivate the leave page confirm message in order to be or not displayed
     * @param active boolean true to activate the leave page confirm message, false otherwise
     */
    function activate(active: boolean) {
      if (active) {
        state.count++;
      } else {
        state.count--;
      }
    }

    /**
     * Show the leave page confirm message
     * @param toFullPath the page to redirect to after confirm
     */
    function show(toFullPath: string) {
      state.toFullPath = toFullPath;
      state.shown = true;
    }

    /**
     * Check if the leave page is confirmed or not
     * @returns true if the user has confirmed leaving the page, false otherwise
     */
    function confirmed(): boolean {
      return !!state.toFullPath;
    }

    /**
     * Confirm the leave page
     */
    function confirm() {
      if (state.toFullPath) {
        router.push(state.toFullPath);
      }
    }

    /**
     * Cancel the leave page
     */
    function cancel() {
      state.toFullPath = null;
    }

    /**
     * Reset the leave page state
     */
    function reset() {
      state.count = 0;
      state.shown = false;
      state.toFullPath = null;
      state.messageConfirmUnsavedData = DEFAULT_CONFIRM_MESSAGE;
      state.title = DEFAULT_TITLE;
      state.excludedRoutes = [];
    }

    /**
     * Set the confirm message to display
     * @param message
     */
    function setConfirmMessage(message: string) {
      state.messageConfirmUnsavedData = message;
    }

    /**
     * Set the title of the confirmation message to display
     * @param title
     */
    function setTitle(title: string) {
      state.title = title;
    }

    /**
     * Sets the excluded routes for the application.
     * @param excludedRoutes - An array of route paths to exclude.
     */
    function setExcludedRoutes(excludedRoutes: string[]) {
      state.excludedRoutes = excludedRoutes;
    }

    return {
      ...toRefs(state),
      isActivated,
      activate,
      show,
      confirmed,
      confirm,
      cancel,
      reset,
      setConfirmMessage,
      setTitle,
      setExcludedRoutes,
    };
  },
);
